import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bot,
  ArrowRight,
  Calendar,
  CheckCircle,
  Clock,
  Users,
  TrendingUp,
  BookOpen,
  Loader,
} from "../utils/icons";
import { Navigation } from "./Navigation";
import AssessmentCard from "./AssessmentCard";
import AIMythsAccordion from "./AIMythsAccordion";
import SurveyPopup from "./SurveyPopup";
import SEO from "./SEO";
import SearchBox from "./SearchBox";
const BlogTimeline = React.lazy(() => import("./BlogTimeline"));
const EnhancedYouTubeEmbed = React.lazy(() => import("./EnhancedYouTubeEmbed"));
import Footer from "./Footer";
import { supabase } from "../lib/supabase";
import { optimizedFetch, prefetchData } from "../utils/data-fetching";
import { initializeVideoOptimization } from "../utils/video-optimization";
import { initializeVideoPerformanceAnalytics } from "../utils/video-performance-analytics";
import OptimizedImage from "./OptimizedImage";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  image_url: string;
  published_at: string;
  blog_categories?: {
    name: string;
    slug: string;
  };
}

interface LandingPageProps {
  onContactClick?: () => void;
  onCalClick?: () => void;
}

interface AgentCardProps {
  title: string;
  description: string;
  link?: string;
  buttonText?: string;
  onButtonClick?: () => void;
  customLink?: string;
}

// Enhanced organization schema with more local business details
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "One80Labs - Omaha AI Training & Consulting",
  description:
    "The Midwest's Premier AI Agency specializing in free AI training workshops, fractional project management, and technology consulting for businesses in Omaha, Nebraska, Council Bluffs, Iowa, and across the Midwest.",
  url: "https://one80labs.com",
  logo: "https://one80labs.com/logo.png",
  address: {
    "@type": "PostalAddress",
    streetAddress: "4383 Nicholas Street #302",
    addressLocality: "Omaha",
    addressRegion: "NE",
    postalCode: "68181",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 41.2651,
    longitude: -95.9397,
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+1-402-915-3613",
      contactType: "customer service",
      email: "hello@one80labs.com",
      areaServed: [
        "US",
        "Nebraska",
        "Iowa",
        "Missouri",
        "Kansas",
        "South Dakota",
        "Omaha",
        "Lincoln",
        "Council Bluffs",
        "Des Moines",
        "Sioux City",
        "Sioux Falls",
      ],
    },
    {
      "@type": "ContactPoint",
      telephone: "+1-402-915-3613",
      contactType: "sales",
      email: "hello@one80labs.com",
      areaServed: [
        "Omaha",
        "Lincoln",
        "Council Bluffs",
        "Des Moines",
        "Kansas City",
        "Bellevue",
        "Papillion",
        "La Vista",
        "Fremont",
        "Grand Island",
      ],
    },
  ],
  sameAs: [
    "https://www.linkedin.com/company/one80labs",
    "https://twitter.com/one80labs",
    "https://www.facebook.com/one80labs",
    "https://www.youtube.com/channel/one80labs",
  ],
  founder: {
    "@type": "Person",
    name: "Nick Kramer",
    jobTitle: "AI Productivity Specialist & Technology Educator",
    image:
      "https://raw.githubusercontent.com/ndkramer/One80Labs-Site/main/public/images/team/nick.kramer.png",
  },
  areaServed: {
    "@type": "GeoCircle",
    geoMidpoint: {
      "@type": "GeoCoordinates",
      latitude: 41.2651,
      longitude: -95.9397,
    },
    geoRadius: "500",
    description:
      "Serving Omaha, Lincoln, Council Bluffs, Bellevue, Papillion, La Vista, and the greater Midwest region including Nebraska, Iowa, Kansas, Missouri, and South Dakota",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "AI Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Free AI Training Workshops in Omaha",
          description:
            "Free hands-on training for ChatGPT, Gemini, and Microsoft 365 Copilot for businesses and professionals in Omaha and the Midwest",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Fractional Project Management in Nebraska",
          description:
            "Expert project leadership on a flexible, part-time basis for Omaha and Midwest businesses",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Custom AI Agent Development for Midwest Businesses",
          description:
            "Tailored AI solutions for business automation and productivity in Omaha, Nebraska and surrounding areas",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Free AI Business Audit",
          description:
            "Complimentary AI assessment to identify opportunities for automation and efficiency in your Omaha business",
          price: "0",
          priceCurrency: "USD",
        },
      },
    ],
  },
};

// Enhanced local business schema with more geographic details
const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "One80Labs - Omaha AI Training & Consulting Agency",
  image: "https://one80labs.com/logo.png",
  description:
    "The Midwest's Premier AI Agency providing free AI training workshops, project management, and technology consulting services in Omaha, Nebraska, Council Bluffs, Iowa, and surrounding areas.",
  "@id": "https://one80labs.com",
  url: "https://one80labs.com",
  telephone: "+1-402-915-3613",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "4383 Nicholas Street #302",
    addressLocality: "Omaha",
    addressRegion: "NE",
    postalCode: "68181",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 41.2651,
    longitude: -95.9397,
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "17:00",
  },
  areaServed: [
    {
      "@type": "City",
      name: "Omaha",
      sameAs: "https://en.wikipedia.org/wiki/Omaha,_Nebraska",
    },
    {
      "@type": "City",
      name: "Lincoln",
      sameAs: "https://en.wikipedia.org/wiki/Lincoln,_Nebraska",
    },
    {
      "@type": "City",
      name: "Council Bluffs",
      sameAs: "https://en.wikipedia.org/wiki/Council_Bluffs,_Iowa",
    },
    {
      "@type": "City",
      name: "Bellevue",
      sameAs: "https://en.wikipedia.org/wiki/Bellevue,_Nebraska",
    },
    {
      "@type": "City",
      name: "Papillion",
      sameAs: "https://en.wikipedia.org/wiki/Papillion,_Nebraska",
    },
    {
      "@type": "City",
      name: "La Vista",
      sameAs: "https://en.wikipedia.org/wiki/La_Vista,_Nebraska",
    },
    {
      "@type": "State",
      name: "Nebraska",
      sameAs: "https://en.wikipedia.org/wiki/Nebraska",
    },
    {
      "@type": "State",
      name: "Iowa",
      sameAs: "https://en.wikipedia.org/wiki/Iowa",
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "AI Services in Omaha",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Free AI Training Workshops in Omaha",
          description:
            "Free hands-on AI training for businesses in Omaha, Council Bluffs, and throughout Nebraska and Iowa",
          price: "0",
          priceCurrency: "USD",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Fractional Project Management in Nebraska",
          description:
            "Expert project leadership for Omaha and Midwest businesses at a fraction of the cost of a full-time hire",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Free AI Business Audit for Omaha Companies",
          description:
            "Complimentary assessment to identify where your Omaha business can save time and money with AI",
          price: "0",
          priceCurrency: "USD",
        },
      },
    ],
  },
};

// Enhanced video schema with more geographic details
const videoSchema = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: "AI Business Transformation",
  description:
    "Learn how AI can transform your business operations in Omaha, Nebraska and Council Bluffs, Iowa. This video explains how One80Labs helps small and mid-size businesses in the Midwest save time, cut costs, and work smarter with artificial intelligence.",
  thumbnailUrl: "https://i.ytimg.com/vi/WjHWcaLFgeI/maxresdefault.jpg",
  uploadDate: "2023-09-15T08:00:00+08:00",
  contentUrl: "https://www.youtube.com/watch?v=WjHWcaLFgeI",
  embedUrl: "https://www.youtube.com/embed/WjHWcaLFgeI",
  duration: "PT3M42S",
  publisher: {
    "@type": "Organization",
    name: "One80Labs",
    logo: {
      "@type": "ImageObject",
      url: "https://one80labs.com/logo.png",
      width: 600,
      height: 60,
    },
  },
  regionsAllowed: "US,NE,IA,MO,KS,SD",
  keywords:
    "AI training Omaha, free ChatGPT workshop, business automation Nebraska, AI consulting Council Bluffs, Midwest AI agency, free AI assessment",
};

// Enhanced service schema for the free AI audit
const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Free AI Business Audit for Omaha Companies",
  provider: {
    "@type": "LocalBusiness",
    name: "One80Labs - Omaha AI Agency",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
  },
  description:
    "Free AI audit to identify where your Omaha, Nebraska or Council Bluffs, Iowa business can save time, cut costs, and work smarter with artificial intelligence. Our complimentary assessment helps Midwest businesses find opportunities for automation and efficiency.",
  areaServed: {
    "@type": "GeoCircle",
    geoMidpoint: {
      "@type": "GeoCoordinates",
      latitude: 41.2651,
      longitude: -95.9397,
    },
    geoRadius: "500",
    description:
      "Serving Omaha, Lincoln, Bellevue, Papillion, La Vista, Council Bluffs, and the greater Midwest region including Nebraska, Iowa, Kansas, Missouri, and South Dakota",
  },
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    validFrom: "2025-01-01",
  },
  serviceOutput: {
    "@type": "Thing",
    name: "AI Implementation Recommendations",
    description:
      "Personalized recommendations for implementing AI in your business to improve efficiency and reduce costs",
  },
};

// Enhanced FAQ schema with more local context and advanced AEO
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Is AI too complex for employees to learn?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No, modern AI tools are increasingly user-friendly and designed to be accessible without deep technical knowledge. Our free training programs in Omaha, Nebraska and Council Bluffs, Iowa help businesses implement AI tools that employees can master quickly. 89% of small businesses now successfully use AI tools to automate routine tasks. Our free 3-hour ChatGPT workshop is specifically designed for beginners with no technical background.",
      },
    },
    {
      "@type": "Question",
      name: "Does AI adoption require a massive upfront investment?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No, many affordable or even free AI tools are available, making AI accessible to businesses with limited budgets in Omaha, Council Bluffs, and throughout Nebraska and Iowa. Our Midwest-based consultants can help you identify cost-effective AI solutions that deliver immediate ROI. 75% of small and medium-sized businesses are already experimenting with AI. Our free AI audit helps identify where you can implement AI without significant investment.",
      },
    },
    {
      "@type": "Question",
      name: "Will AI replace jobs rather than enhance them?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI typically automates repetitive tasks, allowing employees to focus on more strategic activities. Our clients in Omaha, Lincoln, Council Bluffs and throughout Nebraska and Iowa report that AI implementation leads to job transformation rather than elimination. 91% of small businesses using AI believe it will help them grow. Our free AI training workshops show how to use AI as a productivity tool that enhances human capabilities.",
      },
    },
    {
      "@type": "Question",
      name: "Is AI just a trend that won't last?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No, AI is becoming increasingly integrated across various sectors. The global AI market is projected to grow from $58.3 billion in 2021 to $309.6 billion by 2026. Businesses in Omaha, Council Bluffs, and across Nebraska and Iowa are increasingly adopting AI to remain competitive. Our free AI workshops help Midwest businesses stay ahead of this transformative technology.",
      },
    },
    {
      "@type": "Question",
      name: "How can Omaha businesses benefit from AI implementation?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Omaha and Council Bluffs businesses can benefit from AI through automated customer service, streamlined operations, data-driven decision making, and enhanced productivity. Our local expertise helps Nebraska and Iowa companies implement AI solutions tailored to their specific industry needs. Our free AI audit identifies the most impactful areas for implementation in your specific business.",
      },
    },
    {
      "@type": "Question",
      name: "What AI training options are available in Omaha?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "One80Labs offers free and paid AI training workshops in Omaha, Nebraska and Council Bluffs, Iowa, including our free 3-hour ChatGPT workshop and comprehensive courses on Google Gemini and Microsoft 365 Copilot. These hands-on sessions are designed for professionals at all technical levels and can be delivered in-person or virtually throughout the Midwest region.",
      },
    },
    {
      "@type": "Question",
      name: "What is Fractional Project Management and how does it work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Fractional Project Management provides Omaha and Council Bluffs organizations with experienced project leadership on a part-time, flexible basis. It's ideal for Nebraska and Iowa businesses that need project expertise but can't justify a full-time hire, offering 40-60% cost savings compared to traditional hiring. Our free AI audit can help determine if this approach is right for your organization.",
      },
    },
    {
      "@type": "Question",
      name: "Is the ChatGPT workshop really free?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, our 3-hour Intro to AI with ChatGPT workshop is completely free for businesses and professionals in Omaha, Council Bluffs, and throughout the Midwest. This hands-on training provides a solid foundation in AI concepts and practical skills with no cost or obligation.",
      },
    },
    {
      "@type": "Question",
      name: "How do I register for the free AI training in Omaha?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You can register for our free 3-hour AI training workshop by visiting our website and clicking on the 'Learn More & Register' button for the free ChatGPT workshop. We offer both in-person sessions in Omaha and virtual options for participants throughout Nebraska, Iowa, and the Midwest region.",
      },
    },
    // Advanced AEO - Tool-specific questions
    {
      "@type": "Question",
      name: "What is ChatGPT and how can it help my business?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "ChatGPT is an AI language model that can help Omaha businesses with content creation, customer service automation, data analysis, and workflow optimization. Our free ChatGPT workshop in Omaha teaches you how to write effective prompts, automate routine tasks, and integrate ChatGPT into your daily operations. ChatGPT can generate emails, reports, marketing copy, and answer customer questions 24/7.",
      },
    },
    {
      "@type": "Question",
      name: "What is Google Gemini and how does it differ from ChatGPT?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Google Gemini is a multimodal AI model that can process text, images, and code simultaneously. Unlike ChatGPT, Gemini can analyze images, create visual content, and work with Google Workspace applications. Our AI training in Omaha covers both ChatGPT and Gemini, helping you choose the right tool for specific tasks. Gemini excels at data analysis, visual content creation, and Google Workspace integration.",
      },
    },
    {
      "@type": "Question",
      name: "What is Microsoft 365 Copilot and how can it improve productivity?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Microsoft 365 Copilot is an AI assistant integrated into Office applications that can help Omaha businesses create documents, analyze data, generate presentations, and manage emails more efficiently. Our Microsoft 365 Copilot training in Omaha teaches you how to use AI to automate Excel formulas, create PowerPoint presentations, draft emails, and summarize meetings. Copilot can save 2-3 hours per day for typical office workers.",
      },
    },
    // Advanced AEO - Industry-specific questions
    {
      "@type": "Question",
      name: "How can AI help healthcare businesses in Omaha?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI can help Omaha healthcare businesses with patient scheduling, medical record management, diagnostic assistance, and administrative automation. Our AI training for healthcare professionals in Omaha covers HIPAA-compliant AI tools, patient communication automation, and medical documentation assistance. AI can reduce administrative burden by 30-40% while improving patient care quality.",
      },
    },
    {
      "@type": "Question",
      name: "How can AI benefit manufacturing companies in Nebraska?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI can help Nebraska manufacturing companies with predictive maintenance, quality control, supply chain optimization, and production planning. Our AI training for manufacturers in Omaha covers IoT integration, predictive analytics, and automated quality inspection. AI can reduce downtime by 25% and improve production efficiency by 15-20%.",
      },
    },
    {
      "@type": "Question",
      name: "How can AI help financial services companies in Council Bluffs?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI can help Council Bluffs financial services companies with fraud detection, customer service automation, risk assessment, and compliance monitoring. Our AI training for financial professionals covers automated document processing, customer chatbot implementation, and regulatory compliance automation. AI can improve fraud detection accuracy by 90% and reduce processing time by 60%.",
      },
    },
    {
      "@type": "Question",
      name: "How can AI help retail businesses in the Midwest?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI can help Midwest retail businesses with inventory management, customer personalization, demand forecasting, and automated marketing. Our AI training for retail professionals in Omaha covers customer behavior analysis, automated email campaigns, and inventory optimization. AI can increase sales by 15-25% and reduce inventory costs by 20%.",
      },
    },
    // Advanced AEO - Implementation questions
    {
      "@type": "Question",
      name: "How long does it take to implement AI in my business?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI implementation timeline varies by business size and complexity. Simple AI tools like ChatGPT can be implemented in 1-2 weeks, while comprehensive AI systems may take 2-3 months. Our free AI audit in Omaha helps identify quick wins and long-term opportunities. Most businesses see ROI within 30-60 days of AI implementation.",
      },
    },
    {
      "@type": "Question",
      name: "What is the ROI of AI implementation for small businesses?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Small businesses typically see 200-300% ROI on AI implementation within 6-12 months. Our clients in Omaha and Council Bluffs report 20-40% time savings, 15-25% cost reductions, and 30-50% productivity improvements. Our free AI audit helps identify specific ROI opportunities for your business type and size.",
      },
    },
    {
      "@type": "Question",
      name: "How do I choose the right AI tools for my business?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Choosing the right AI tools depends on your business needs, budget, and technical capabilities. Our free AI audit in Omaha analyzes your current processes and identifies the most impactful AI solutions. We recommend starting with user-friendly tools like ChatGPT and Microsoft 365 Copilot, then scaling to more advanced solutions as your team becomes comfortable with AI.",
      },
    },
    {
      "@type": "Question",
      name: "What are the security concerns with AI implementation?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI security concerns include data privacy, intellectual property protection, and compliance requirements. Our AI training in Omaha covers security best practices, data handling protocols, and compliance considerations. We help businesses implement AI safely while maintaining data security and regulatory compliance. Most AI tools offer enterprise-grade security features.",
      },
    },
  ],
};

// Add a course schema for the free workshop
const courseSchema = {
  "@context": "https://schema.org",
  "@type": "Course",
  name: "Discover the Power of AI with ChatGPT – Free 3-Hour Workshop",
  description:
    "AI Foundations: Getting Started with ChatGPT for Productivity & Innovation. Join our free 3-hour hands-on workshop designed to help professionals in Omaha and the Midwest understand, experiment with, and apply AI tools.",
  provider: {
    "@type": "Organization",
    name: "One80Labs",
    sameAs: "https://one80labs.com",
  },
  url: "https://one80labs.com/course/free-chatgpt-workshop",
  hasCourseInstance: [
    {
      "@type": "CourseInstance",
      courseMode: "OnlineCourse",
      startDate: "2025-06-19",
      endDate: "2025-06-19",
      location: {
        "@type": "VirtualLocation",
        name: "Online",
      },
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
      },
    },
    {
      "@type": "CourseInstance",
      courseMode: "OnSite",
      startDate: "2025-06-17",
      endDate: "2025-06-17",
      location: {
        "@type": "Place",
        name: "Omaha Location",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Omaha",
          addressRegion: "NE",
          addressCountry: "US",
        },
      },
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
      },
    },
  ],
  timeRequired: "PT3H",
  educationalLevel: "Beginner",
  teaches: [
    "AI fundamentals",
    "ChatGPT basics",
    "Prompt engineering",
    "Business automation",
    "Productivity enhancement",
  ],
};
const AgentCard: React.FC<AgentCardProps> = ({
  title,
  description,
  link,
  buttonText,
  onButtonClick,
  customLink,
}) => (
  <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col h-full">
    <div className="bg-gray-50 p-4 border-b border-gray-200 min-h-[80px] flex items-center">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="p-6 text-gray-600 mb-2 flex-grow relative overflow-hidden">
      {(title === "Your Bot Here" || title === "Your Agent Here") && (
        <div className="absolute transform rotate-45 text-center bg-[#F98B3D] text-white py-1 px-12 right-[-35px] top-[20px] shadow-md">
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Coming Soon
        </div>
      )}
      <div dangerouslySetInnerHTML={{ __html: description }} />
    </div>
    <div className="px-6 pb-6">
      {link ? (
        <Link
          to={link}
          className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md flex items-center justify-center transition-colors duration-200"
        >
          {title === "AI Training" ? "Course Catalog" : "Try Agent"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      ) : onButtonClick ? (
        <button
          onClick={onButtonClick}
          className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md flex items-center justify-center transition-colors duration-200"
        >
          {buttonText || "Learn More"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </button>
      ) : (
        <Link
          to={
            customLink ||
            (title ===
            "Google Gemini Essentials: Smarter Documents, Data, and Communication"
              ? "/course/google-gemini-essentials"
              : title === "Unlocking ChatGPT: Work Smarter, Not Harder"
                ? "/chatgpt-workshop"
                : title ===
                    "Microsoft 365 Copilot Unleashed: AI-Driven Productivity"
                  ? "/microsoft365-copilot"
                  : title ===
                      "Work Smarter with AI: Automate, Optimize & Stay Ahead"
                    ? "/ai-workshop"
                    : "/courses")
          }
          className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md flex items-center justify-center transition-colors duration-200"
        >
          View Course Details
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      )}
    </div>
  </div>
);

function LandingPage({ onContactClick, onCalClick }: LandingPageProps) {
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const agentsRef = useRef<HTMLDivElement>(null);
  const trainingRef = useRef<HTMLDivElement>(null);
  const pmRef = useRef<HTMLDivElement>(null);
  const [aiAuditSurveyId, setAiAuditSurveyId] = useState<string | null>(null);
  const [showSurveyPopup, setShowSurveyPopup] = useState(false);
  const [surveyData, setSurveyData] = useState<{
    survey: any;
    questions: any[];
    choices: Record<string, any[]>;
  } | null>(null);
  const [videoInView, setVideoInView] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);

  // Memoize heavy schema objects to prevent recreation on every render
  const memoizedSchemas = React.useMemo(
    () => [
      organizationSchema,
      localBusinessSchema,
      videoSchema,
      serviceSchema,
      faqSchema,
      courseSchema,
    ],
    [],
  );

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      const headerHeight = document.querySelector("header")?.offsetHeight || 0;
      const elementPosition =
        ref.current.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight - 20;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Memoize survey data fetching to prevent multiple calls
  const fetchSurveyDataMemoized = React.useCallback(
    async (surveyId: string) => {
      try {
        const { data: surveyData, error: surveyError } = await supabase
          .from("surveys")
          .select("*")
          .eq("id", surveyId)
          .single();

        if (surveyError) {
          console.error("Error fetching survey:", surveyError);
          return;
        }

        const { data: questionsData, error: questionsError } = await supabase
          .from("questions")
          .select("*")
          .eq("survey_id", surveyId)
          .order("order_number");

        if (questionsError) {
          console.error("Error fetching questions:", questionsError);
          return;
        }

        const multipleChoiceQuestions = questionsData.filter(
          (q) => q.question_type === "multiple_choice",
        );

        const choicesMap: Record<string, any[]> = {};

        if (multipleChoiceQuestions.length > 0) {
          for (const question of multipleChoiceQuestions) {
            const { data: choicesData, error: choicesError } = await supabase
              .from("choices")
              .select("*")
              .eq("question_id", question.id)
              .order("order_number");

            if (choicesError) {
              console.error(
                `Error fetching choices for question ${question.id}:`,
                choicesError,
              );
              continue;
            }

            choicesMap[question.id] = choicesData;
          }
        }

        setSurveyData({
          survey: surveyData,
          questions: questionsData,
          choices: choicesMap,
        });

        console.log("Survey data pre-fetched successfully");
      } catch (err) {
        console.error("Error pre-fetching survey data:", err);
      }
    },
    [],
  );

  useEffect(() => {
    const initializePageData = async () => {
      try {
        // Single optimized call for survey data
        const data = await optimizedFetch(
          "prefetch_ai_audit_survey",
          async () => {
            const { data, error } = await supabase
              .from("surveys")
              .select("id")
              .eq("is_active", true)
              .ilike("title", "%AI Audit%")
              .single();

            if (error) throw error;
            return data;
          },
          { cacheKey: "prefetch_ai_audit_survey", ttl: 300000 }, // 5 minutes
        );

        if (data) {
          setAiAuditSurveyId(data.id);
          // Only fetch survey data when actually needed, not preemptively
        }

        // Prefetch other data for better UX
        prefetchData.blogPosts();
        prefetchData.courses();

        // Initialize video optimization and preload critical videos
        initializeVideoOptimization();

        // Preload the hero video thumbnail for faster loading
        const heroVideoId = "WjHWcaLFgeI";
        const thumbnailImg = new Image();
        thumbnailImg.src = `https://img.youtube.com/vi/${heroVideoId}/maxresdefault.jpg`;
        thumbnailImg.onload = () =>
          console.log("📷 Hero video thumbnail preloaded");

        // Initialize video performance analytics
        initializeVideoPerformanceAnalytics();
      } catch (err) {
        console.error("Error initializing page data:", err);
      }
    };

    initializePageData();
  }, []);

  // Intersection Observer for video loading optimization
  useEffect(() => {
    if (!videoRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !videoInView) {
          setVideoInView(true);
          console.log("🎬 Video entered viewport, optimizing loading");
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of video is visible
        rootMargin: "50px", // Start loading 50px before video comes into view
      },
    );

    observer.observe(videoRef.current);

    return () => {
      observer.disconnect();
    };
  }, [videoInView]);

  // Removed duplicate fetchSurveyData - using memoized version above

  // Removed duplicate useEffect - survey ID is now fetched in main initialization

  const handleAuditButtonClick = () => {
    console.log("Audit button clicked");
    setSurveyLoading(true);

    setShowSurveyPopup(true);

    if (!surveyData && aiAuditSurveyId) {
      console.log("Fetching survey data for ID:", aiAuditSurveyId);
      fetchSurveyDataMemoized(aiAuditSurveyId)
        .then(() => {
          console.log("Survey data fetched successfully");
          setSurveyLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching survey data:", err);
          setSurveyLoading(false);
        });
    } else if (!aiAuditSurveyId) {
      console.log("Fetching survey ID");
      supabase
        .from("surveys")
        .select("id")
        .eq("is_active", true)
        .ilike("title", "%AI Audit%")
        .single()
        .then(({ data, error }) => {
          if (error) {
            console.error("Error fetching survey ID:", error);
            setSurveyLoading(false);
            return;
          }

          if (data) {
            console.log("Survey ID fetched:", data.id);
            setAiAuditSurveyId(data.id);
            return fetchSurveyDataMemoized(data.id);
          }
        })
        .then(() => {
          console.log("Survey data fetched after ID lookup");
          setSurveyLoading(false);
        })
        .catch((err) => {
          console.error("Error in survey ID/data fetch chain:", err);
          setSurveyLoading(false);
        });
    } else {
      console.log("Using existing survey data");
      setSurveyLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Free AI Training & Business Audit in Omaha, NE | One80Labs"
        description="Get a free AI audit for your Omaha business and discover our free ChatGPT training workshop. We help Nebraska and Iowa businesses save time, cut costs, and work smarter with AI."
        image="https://images.unsplash.com/photo-1677442135968-6d89485dd1d1"
        jsonLd={memoizedSchemas}
        keywords={[
          "free AI training Omaha",
          "free ChatGPT workshop Nebraska",
          "AI business audit Omaha",
          "AI consulting Council Bluffs",
          "free AI assessment Nebraska",
          "AI productivity Midwest",
          "ChatGPT training Omaha",
          "business automation Nebraska",
          "AI implementation Iowa",
          "Omaha technology consulting",
          "Nebraska AI agency",
          "Council Bluffs AI training",
          "Midwest AI solutions",
          "Omaha fractional project management",
          "free AI workshop Nebraska",
          "AI cost savings Omaha",
          "business efficiency Iowa",
          "AI for small business Nebraska",
          "Omaha AI experts",
          "local AI training Midwest",
        ]}
      />
      <Navigation onSearchClick={() => setShowSearch(!showSearch)} />
      {showSearch && (
        <div className="max-w-3xl mx-auto px-4 py-3">
          <SearchBox />
        </div>
      )}

      {/* Hero Section */}
      <div className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* AEO-optimized heading with voice search keywords */}
          <h1 className="text-2xl sm:text-1xl md:text-6xl font-bold text-gray-900 mb-6">
            Free AI Audit: See Where Your Business Can Save Time, Cut Costs, and
            Work Smarter
          </h1>
          {/* AEO-optimized description with clear company definition */}
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            One80Labs helps small and mid-size businesses in Omaha and the
            Midwest harness the power of AI to streamline operations, reduce
            expenses, and stay ahead of the competition. We provide AI training,
            custom development, and fractional project management services.
          </p>

          <div
            ref={videoRef}
            className="max-w-3xl mx-auto mb-8"
            id="hero-video"
          >
            <React.Suspense
              fallback={
                <div className="w-full h-[400px] bg-gray-200 animate-pulse mx-auto rounded-lg"></div>
              }
            >
              <EnhancedYouTubeEmbed
                videoId="WjHWcaLFgeI"
                title="AI Business Transformation"
                lazyLoad={false}
                showThumbnail={true}
                enableAdvancedOptimizations={false}
                onLoadStart={() => console.log("🎬 Video loading started")}
                onLoadComplete={() => console.log("✅ Video loading completed")}
              />
            </React.Suspense>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleAuditButtonClick}
              className="bg-[#F98B3D] text-white px-6 py-3 rounded-md font-medium hover:bg-[#e07a2c] transition-colors duration-200 flex items-center"
              type="button"
            >
              {surveyLoading ? (
                <>
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                  Loading...
                </>
              ) : (
                "Start Your Free AI Audit"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* AEO-optimized AI Services Section */}
      <div ref={agentsRef} className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            What Services Does One80Labs Offer?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl">
            One80Labs provides three core services: AI Training workshops,
            Custom AI Development, and Fractional Project Management. Each
            service is designed to help businesses implement AI effectively and
            efficiently.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AgentCard
              title="AI Training"
              description="<b>Train Your Team to Work Smarter with AI</b>. <br><br>We help teams to use AI tools in a way that's relevant, actionable, and easy to adopt."
              link="https://www.one80labs.com/courses"
            />
            <AgentCard
              title="AI Automation & Development"
              description="<b>From Simple Workflows to Advanced AI Agents</b><br><br>We design AI automation that solves real business problems, eliminates repetitive work, and frees your team to focus on what matters most — working faster, smarter, and with less frustration."
              buttonText="Book your Free Consultation"
              onButtonClick={onCalClick}
            />
            <AgentCard
              title="Fractional PM Services"
              description="<b>Expert Project Leadership—Without the Overhead</b><br><br>Our fractional Project Managers keep your AI projects on track, on time, and aligned with your goals—no full-time hire required."
              buttonText="Contact Us"
              onButtonClick={onContactClick}
            />
          </div>

          {/* New CTA after AI Agents section */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mt-12">
            <div className="flex items-center p-8">
              <div className="flex-shrink-0 bg-gray-100 rounded-full p-4">
                <Bot className="h-8 w-8 text-black" />
              </div>
              <div className="ml-6 flex-grow">
                <h3 className="text-xl font-semibold text-gray-900">
                  Need a Custom AI Agent?
                </h3>
                <p className="text-gray-600 mt-1">
                  Let's build an AI solution tailored to your Omaha business
                  needs
                </p>
              </div>
              <button
                onClick={onCalClick}
                className="flex-shrink-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
              >
                Book Consultation
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* AI Training Section */}
      <div ref={trainingRef} className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">AI Training</h2>

          {/* Featured Course - Full Width */}
          <div className="mb-8 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/3">
                <OptimizedImage
                  src="https://baqoamgonxbvycaruktw.supabase.co/storage/v1/object/public/assets/course/1747930668691-n3s6f36sgt.jpeg"
                  alt="Free ChatGPT Workshop"
                  width={1470}
                  height={256}
                  hero={true}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="md:w-2/3 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Discover the Power of AI with ChatGPT – Free 3-Hour Workshop
                </h3>
                <p className="text-gray-600 mb-4">
                  AI Foundations: Getting Started with ChatGPT for Productivity
                  & Innovation. Join our free 3-hour hands-on workshop designed
                  to help professionals like you understand, experiment with,
                  and apply AI tools—starting with ChatGPT.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Next Sessions
                    </span>
                    <span className="text-sm text-gray-700">June 17, 2025</span>
                    <span className="text-sm text-gray-700">June 18, 2025</span>
                    <span className="text-sm text-gray-700">June 19, 2025</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Format
                    </span>
                    <span className="text-sm text-gray-700">
                      In-person or virtual
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Duration
                    </span>
                    <span className="text-sm text-gray-700">3 hours</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Price
                    </span>
                    <span className="text-sm text-gray-700">Free</span>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Link
                    to="https://www.one80labs.com/course/free-chatgpt-workshop"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] transition-colors duration-200"
                  >
                    Learn More & Register
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <AgentCard
              title="Google Gemini Essentials: Smarter Documents, Data, and Communication"
              description="Supercharge your workflow with Google Gemini and AI-powered automation! This hands-on workshop will teach you how to use Gemini across Google Docs, Sheets, Slides, Gmail, and Meet to streamline communication, generate insights, and optimize productivity. Learn how to automate tasks, enhance collaboration, and make smarter decisions—no technical expertise needed."
              customLink="/course/google-gemini-essentials"
            />
            <AgentCard
              title="Unlocking ChatGPT: Work Smarter, Not Harder"
              description="Boost your productivity with ChatGPT! This hands-on workshop will teach you how to automate tasks, enhance communication, and streamline workflows using AI-powered tools. Learn practical strategies to save time, improve efficiency, and gain a competitive edge—no technical experience required."
              customLink="/course/unlocking-chatgpt"
            />
            <AgentCard
              title="Microsoft 365 Copilot Unleashed: AI-Driven Productivity"
              description="Work smarter with Microsoft 365 Copilot and unlock AI-powered efficiency! This hands-on workshop will show you how to automate emails, create reports, analyze data, and streamline workflows across Word, Excel, Outlook, PowerPoint, and Teams. Learn how to leverage Copilot to save time, boost productivity, and enhance collaboration—no technical skills required."
              customLink="/course/microsoft365-copilot-unleashed"
            />
          </div>

          {/* New CTA after AI Training section */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mt-12">
            <div className="flex items-center p-8">
              <div className="flex-shrink-0 bg-gray-100 rounded-full p-4">
                <BookOpen className="h-8 w-8 text-black" />
              </div>
              <div className="ml-6 flex-grow">
                <h3 className="text-xl font-semibold text-gray-900">
                  Ready to Master AI Tools?
                </h3>
                <p className="text-gray-600 mt-1">
                  Transform your workflow with our hands-on AI training in Omaha
                </p>
              </div>
              <button
                onClick={onContactClick}
                className="flex-shrink-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
              >
                Book Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Fractional PM Services Section */}
      <div ref={pmRef} className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Fractional Project Management Services
          </h2>

          {/* Hero Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm mb-12">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  Expert Project Leadership Without the Full-Time Commitment
                </h3>
                <p className="text-gray-600 mb-6">
                  Our Fractional Project Management services provide the
                  expertise you need, exactly when you need it. We help
                  organizations of all sizes in Omaha and across Nebraska
                  deliver successful projects without the overhead of a
                  full-time hire.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/fractional-pm"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Learn More About Fractional PM
                  </Link>
                </div>
              </div>
              <div className="md:w-1/2">
                <OptimizedImage
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2940&q=80"
                  alt="Team collaborating on project in Omaha"
                  width={1470}
                  height={256}
                  hero={true}
                  className="w-full h-64 object-cover rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* What is Fractional PM */}
          <div className="mb-12">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">
              What is Fractional Project Management?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <p className="text-gray-600 mb-4">
                  Fractional Project Management provides your Omaha organization
                  with experienced project leadership on a part-time, flexible
                  basis. It's the perfect solution for Nebraska businesses that:
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      Need project expertise but can't justify a full-time hire
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      Have fluctuating project workloads throughout the year
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      Want to improve project outcomes without increasing
                      headcount
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      Need specialized expertise for specific initiatives
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  The Fractional Advantage
                </h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-gray-200 p-2 rounded-full mr-4">
                      <Clock className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">
                        Flexible Engagement
                      </h5>
                      <p className="text-gray-600 text-sm">
                        Scale hours up or down based on your project needs
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-gray-200 p-2 rounded-full mr-4">
                      <TrendingUp className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">
                        Cost-Effective
                      </h5>
                      <p className="text-gray-600 text-sm">
                        Pay only for the time and expertise you need
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-gray-200 p-2 rounded-full mr-4">
                      <Users className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">
                        Experienced Talent
                      </h5>
                      <p className="text-gray-600 text-sm">
                        Access to senior-level project managers
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Assessment Card */}
          <div className="mb-12">
            <AssessmentCard onContactClick={onContactClick} />
          </div>

          {/* CTA - Now with white background */}
          <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm">
            <div className="text-center max-w-3xl mx-auto">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Ready to Transform Your Project Delivery?
              </h3>
              <p className="text-gray-600 mb-6">
                Schedule a free consultation to discuss how our Fractional
                Project Management services can help your Omaha organization
                deliver successful projects.
              </p>
              <Link
                to="/fractional-pm"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Learn More About Fractional PM
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* AI Myths & Misconceptions Section */}
      <div className="bg-white py-16" id="ai-myths">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            The Truth about AI: AI Myths & Misconceptions
          </h2>
          <AIMythsAccordion />
        </div>
      </div>

      {/* Latest Updates Timeline Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Blog Timeline
          </h2>
          <React.Suspense
            fallback={
              <div className="w-full h-[300px] bg-gray-200 animate-pulse rounded-lg"></div>
            }
          >
            <BlogTimeline />
          </React.Suspense>
        </div>
      </div>

      <Footer />

      {/* Survey Popup */}
      <SurveyPopup
        key={`survey-popup-${showSurveyPopup}`}
        surveyId={aiAuditSurveyId || "529cfb98-fe63-46bd-bc70-8f151d5b85b8"}
        isOpen={showSurveyPopup}
        onClose={() => setShowSurveyPopup(false)}
        preloadedData={surveyData}
      />
    </div>
  );
}

// Optimize with React.memo to prevent unnecessary re-renders
export default React.memo(LandingPage);
